import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, CalendarCheck, MapPin, ArrowRight, BadgeCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingCallBar from "@/components/FloatingCallBar";
import Seo from "@/components/Seo";
import { SERVICES, LOCATIONS, PHONE_DISPLAY, PHONE_TEL } from "@/constants/site";

export default function LocationPage() {
  const { city } = useParams();
  const location = LOCATIONS.find((l) => l.slug === city);

  if (!location) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAFAFA] px-4 text-center">
        <h1 className="font-display text-2xl font-bold text-navy">Area not found</h1>
        <Link to="/" className="mt-4 rounded-full bg-rescue px-6 py-3 text-sm font-bold text-white">
          Back to homepage
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA] pb-16 md:pb-0" data-testid={`location-page-${location.slug}`}>
      <Seo
        title={`Appliance Repair ${location.city} | GTA Appliance Rescue`}
        description={`Same-day appliance repair in ${location.city}. Refrigerators, washers, dryers, dishwashers, stoves, freezers & microwaves. Call 905-906-6836 today.`}
        canonical={`https://gtaappliancerescue.ca/areas/${location.slug}`}
      />
      <Header />
      <main className="pt-36 sm:pt-40">
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-rescue"
          >
            <MapPin className="h-3.5 w-3.5" />
            Service Area / GTA
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-navy sm:text-5xl"
          >
            Appliance Repair in {location.city}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            {location.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-7 flex flex-col gap-3 sm:flex-row"
          >
            <a
              href={PHONE_TEL}
              data-testid="location-call-button"
              className="flex items-center justify-center gap-2.5 rounded-full bg-rescue px-8 py-4 text-base font-bold text-white shadow-xl shadow-red-600/25 transition-[background-color,transform] duration-200 hover:bg-rescue-dark hover:-translate-y-0.5"
            >
              <Phone className="h-5 w-5" /> Call Now: {PHONE_DISPLAY}
            </a>
            <a
              href="/#request-service"
              data-testid="location-book-button"
              className="flex items-center justify-center gap-2.5 rounded-full border-2 border-navy bg-white px-8 py-4 text-base font-bold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            >
              <CalendarCheck className="h-5 w-5" /> Request Service Online
            </a>
          </motion.div>
          <p className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-slate-600">
            <BadgeCheck className="h-4 w-4 text-rescue" />
            $80 service call — waived when you proceed with the repair · 1-yr parts, 30-day labour warranty
          </p>
        </section>

        <section className="mx-auto mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
            {location.city} Neighbourhoods We Serve
          </h2>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {location.neighbourhoods.map((n) => (
              <span
                key={n}
                className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-navy"
              >
                <MapPin className="h-3.5 w-3.5 text-rescue" /> {n}
              </span>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
            What We Repair in {location.city}
          </h2>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                >
                  <Link
                    to={`/services/${service.slug}`}
                    data-testid={`location-service-${service.slug}`}
                    className="group flex h-full items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-[border-color,box-shadow] duration-200 hover:border-rescue/40 hover:shadow-lg hover:shadow-red-600/5"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="flex items-center gap-1.5 font-display text-base font-bold text-navy">
                        {service.name}
                        <ArrowRight className="h-4 w-4 text-rescue transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                      <span className="mt-1 block text-sm leading-relaxed text-slate-600">
                        {service.tagline}
                      </span>
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      </main>
      <div className="mt-20">
        <Footer />
      </div>
      <FloatingCallBar />
    </div>
  );
}
